const desktopQuery = window.matchMedia('(min-width: 75rem)');
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const navHistory = new WeakMap();
const MOTION_MS = 420;
const MOTION_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

const play = (element, keyframes) => {
  if (!element || reduceMotion.matches) return Promise.resolve(true);
  const id = (Number(element.dataset.motion) || 0) + 1;
  element.dataset.motion = String(id);
  element.getAnimations().forEach((animation) => animation.cancel());
  const animation = element.animate(keyframes, {
    duration: MOTION_MS,
    easing: MOTION_EASE,
    fill: 'both',
  });
  const done = animation.finished
    .then(() => Number(element.dataset.motion) === id)
    .catch(() => false);
  const timeout = new Promise((resolve) => {
    window.setTimeout(() => resolve(false), MOTION_MS + 700);
  });
  return Promise.race([done, timeout]);
};

const navDialog = () => document.getElementById('header-nav-panel');

const setExpanded = (selector, expanded) => {
  for (const button of document.querySelectorAll(selector)) {
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  }
};

const syncPanes = (dialog) => {
  for (const pane of dialog.querySelectorAll('[data-nav-pane]')) {
    pane.inert = !pane.classList.contains('is-active');
  }
};

const resetPanes = (dialog) => {
  const panes = [...dialog.querySelectorAll('[data-nav-pane]')];
  const root = panes[0];
  for (const pane of panes) {
    pane.classList.remove('is-active', 'is-leave-left');
  }
  root?.classList.add('is-active');
  navHistory.set(dialog, root ? [root] : []);
  syncPanes(dialog);
};

const settlePane = (dialog, pane, token) => {
  if (token !== pane.dataset.motion) return;
  syncPanes(dialog);
  const focusTarget = pane.querySelector('[data-nav-back]') || pane.querySelector('a, button');
  focusTarget?.focus({ preventScroll: true });
};

const goForward = (dialog, target) => {
  const stack = navHistory.get(dialog) || [];
  const current = stack[stack.length - 1];
  if (!current || current === target) return;
  current.classList.remove('is-active');
  current.classList.add('is-leave-left');
  target.classList.add('is-active');
  const scroller = target.querySelector('.header-nav__pane-scroll');
  if (scroller) scroller.scrollTop = 0;
  stack.push(target);
  navHistory.set(dialog, stack);
  play(current, [
    { transform: 'translate3d(0, 0, 0)' },
    { transform: 'translate3d(-100%, 0, 0)' },
  ]);
  play(target, [
    { transform: 'translate3d(100%, 0, 0)' },
    { transform: 'translate3d(0, 0, 0)' },
  ]).then((finished) => {
    if (finished) settlePane(dialog, target, target.dataset.motion);
  });
};

const goBack = (dialog) => {
  const stack = navHistory.get(dialog) || [];
  if (stack.length < 2) return;
  const current = stack.pop();
  const previous = stack[stack.length - 1];
  current.classList.remove('is-active');
  previous.classList.remove('is-leave-left');
  previous.classList.add('is-active');
  navHistory.set(dialog, stack);
  play(current, [
    { transform: 'translate3d(0, 0, 0)' },
    { transform: 'translate3d(100%, 0, 0)' },
  ]);
  play(previous, [
    { transform: 'translate3d(-100%, 0, 0)' },
    { transform: 'translate3d(0, 0, 0)' },
  ]).then((finished) => {
    if (finished) settlePane(dialog, previous, previous.dataset.motion);
  });
};

let navClosing = false;

const openNav = () => {
  const dialog = navDialog();
  if (!dialog || dialog.open) return;
  bindNavDialog(dialog);
  resetPanes(dialog);
  dialog.showModal();
  setExpanded('[data-header-menu]', true);
  const panel = dialog.querySelector('.header-nav__panel');
  const scrim = dialog.querySelector('[data-nav-scrim]');
  panel?.getBoundingClientRect();
  const motion = Promise.all([
    play(panel, [
      { transform: 'translate3d(100%, 0, 0)' },
      { transform: 'translate3d(0, 0, 0)' },
    ]),
    play(scrim, [{ opacity: 0 }, { opacity: 1 }]),
  ]);
  motion.then((results) => {
    if (!dialog.open || !results.every(Boolean)) return;
    dialog.classList.add('is-open');
    panel?.getAnimations().forEach((animation) => animation.cancel());
    scrim?.getAnimations().forEach((animation) => animation.cancel());
    dialog.querySelector('[data-nav-focus]')?.focus({ preventScroll: true });
  });
};

const closeNav = () => {
  const dialog = navDialog();
  if (!dialog?.open || navClosing) return;
  navClosing = true;
  setExpanded('[data-header-menu]', false);
  const opener = document.querySelector('[data-header-menu]');
  const panel = dialog.querySelector('.header-nav__panel');
  const scrim = dialog.querySelector('[data-nav-scrim]');

  const finish = () => {
    if (!navClosing) return;
    navClosing = false;
    dialog.classList.remove('is-open');
    panel?.getAnimations().forEach((animation) => animation.cancel());
    scrim?.getAnimations().forEach((animation) => animation.cancel());
    if (dialog.open) dialog.close();
    resetPanes(dialog);
    opener?.focus({ preventScroll: true });
  };

  if (reduceMotion.matches) {
    finish();
    return;
  }

  Promise.all([
    play(panel, [
      { transform: 'translate3d(0, 0, 0)' },
      { transform: 'translate3d(100%, 0, 0)' },
    ]),
    play(scrim, [{ opacity: 1 }, { opacity: 0 }]),
  ]).then(finish);
};

const openSearch = (panel, button) => {
  panel.hidden = false;
  panel.getBoundingClientRect();
  panel.classList.add('is-open');
  button.setAttribute('aria-expanded', 'true');
  panel.querySelector('input')?.focus();
};

const closeSearch = (panel, button) => {
  if (!panel || panel.hidden) return;
  panel.classList.remove('is-open');
  button?.setAttribute('aria-expanded', 'false');

  const finish = () => {
    panel.hidden = true;
  };

  if (reduceMotion.matches) {
    finish();
    return;
  }

  panel.addEventListener('transitionend', finish, { once: true });
  window.setTimeout(finish, 320);
};

const closeDesktopMenus = () => {
  for (const item of document.querySelectorAll('.header-nav__item.is-open')) {
    item.classList.remove('is-open');
    item.querySelector('[data-nav-caret]')?.setAttribute('aria-expanded', 'false');
  }
};

const bindNavDialog = (dialog) => {
  if (!dialog || dialog.dataset.navBound === 'true') return;
  dialog.dataset.navBound = 'true';

  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    const stack = navHistory.get(dialog) || [];
    if (stack.length > 1) goBack(dialog);
    else closeNav();
  });

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog || event.target.closest('[data-nav-scrim]')) closeNav();
  });
};

const onClick = (event) => {
  const menuButton = event.target.closest('[data-header-menu]');
  if (menuButton) {
    const dialog = navDialog();
    if (dialog?.open) closeNav();
    else openNav();
    return;
  }

  const searchButton = event.target.closest('[data-header-search]');
  if (searchButton) {
    const panel = document.getElementById(searchButton.getAttribute('aria-controls') || '');
    if (!panel) return;
    if (panel.classList.contains('is-open')) closeSearch(panel, searchButton);
    else openSearch(panel, searchButton);
    return;
  }

  const forward = event.target.closest('[data-nav-forward]');
  if (forward) {
    const dialog = navDialog();
    const target = dialog?.querySelector(`#${CSS.escape(forward.dataset.navTarget || '')}`);
    if (dialog && target) goForward(dialog, target);
    return;
  }

  const back = event.target.closest('[data-nav-back]');
  if (back) {
    const dialog = navDialog();
    if (dialog) goBack(dialog);
    return;
  }

  const closer = event.target.closest('[data-nav-close]');
  if (closer) {
    closeNav();
    return;
  }

  const caret = event.target.closest('[data-nav-caret]');
  if (caret) {
    const item = caret.closest('.header-nav__item');
    if (!item) return;
    const willOpen = !item.classList.contains('is-open');
    closeDesktopMenus();
    if (willOpen) {
      item.classList.add('is-open');
      caret.setAttribute('aria-expanded', 'true');
    }
  }
};

if (!window.__sparkyxHeaderUi) {
  window.__sparkyxHeaderUi = true;
  document.addEventListener('click', onClick);
  const dialog = navDialog();
  if (dialog) bindNavDialog(dialog);
}

document.addEventListener('predictive-search:open', closeDesktopMenus);

document.addEventListener('shopify:section:load', () => {
  const dialog = navDialog();
  if (dialog) bindNavDialog(dialog);
});
